import {
	STATEMENT_PDF_CSS,
	STATEMENT_PDF_FONT_FACE_CSS,
	STATEMENT_PDF_TEMPLATE,
	STATEMENT_PDF_TABLE_COLUMN_COUNT,
	STATEMENT_PDF_DEFAULT_TABLE_HEADERS,
} from './statementPdfTemplate.js';

/**
 * 对账单正文 DOM：与 Puppeteer 导出同源。
 * 预览镜像：`frontend/src/screens/dashboard/overdue-statistic/modal/StatementPdfPreviewModal.jsx`
 *（共用 `STATEMENT_PDF_CSS` / scoped 预览 CSS；内联 flex、padding、客户区分栏等若调整须两边对齐，避免版式漂移。）
 */

export const escapeHtml = (value) => {
	if (value === null || value === undefined) return '';
	return String(value).replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#39;');
};

const splitDisplayNumbers = (raw) => {
	const v = String(raw ?? '').trim();
	if (!v || v === '-' || v === '—') return ['—'];
	const parts = v
		.split(/[\n,，、]|(?:\s+\/\s+)/)
		.map((s) => s.trim())
		.filter(Boolean);
	return parts.length > 0 ? parts : ['—'];
};

const numberCellHtml = (raw) => {
	const lines = splitDisplayNumbers(raw);
	return lines.map((line) => `<div>${escapeHtml(line)}</div>`).join('');
};

const paymentStageHtmlWithTermDays = (paymentStageRaw, paymentTermDays) => {
	const base = escapeHtml(paymentStageRaw || '-');
	const n = Number(paymentTermDays);
	if (!Number.isFinite(n)) return base;
	const days = Math.max(0, Math.floor(n));
	return `${base} · 账期${escapeHtml(String(days))}天`;
};

export const formatStatementMoney = (n) => {
	const num = Number(n) || 0;
	return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
};

export const mergeStatementPdfTemplate = (overrides = {}) => {
	const tpl = {
		...STATEMENT_PDF_TEMPLATE,
		companyInfo: {
			name: '',
			line1: '',
			line2: '',
			line3: '',
			contactLine1: '',
			contactLine2: '',
			...(overrides.companyInfo || {}),
		},
		text: {
			documentTitle: '',
			customerTitle: '',
			billTo: '',
			asOfDate: '',
			salesRepLabel: '业务员',
			customerPhoneLabel: '电话',
			customerEmailLabel: '邮箱',
			customerAddressLabel: '地址',
			customerOrganizationLabel: '单位/院系',
			emptyLine: '',
			totalLabel: '',
			remarkLabel: '',
			remarkText: '',
			paymentTitle: '',
			wire: '',
			wireEnterpriseName: '',
			wireTaxId: '',
			wireAddress: '',
			wirePhone: '',
			wireBank: '',
			wireAccount: '',
			check: '',
			checkMail: '',
			checkExpress: '',
			checkLine3: '',
			checkLine4: '',
			tableHeaders: [...STATEMENT_PDF_DEFAULT_TABLE_HEADERS],
			...(overrides.text || {}),
		},
		currencyCode: overrides.currencyCode || '',
	};
	const th = Array.isArray(tpl.text.tableHeaders) ? [...tpl.text.tableHeaders] : [];
	while (th.length < STATEMENT_PDF_TABLE_COLUMN_COUNT) th.push('');
	tpl.text = { ...tpl.text, tableHeaders: th.slice(0, STATEMENT_PDF_TABLE_COLUMN_COUNT) };
	return tpl;
};

/**
 * 与 Puppeteer 导出 PDF 使用同一 HTML（前后端共用）。
 */
const optionalMetaLine = (label, raw) => {
	const v = String(raw ?? '').trim();
	if (!v) return '';
	return `
				<div class="metaLine">${escapeHtml(label)}：${escapeHtml(v)}</div>`;
};

const metaLabelValueDiv = (label, raw) => {
	const v = String(raw ?? '').trim();
	if (!v) return '';
	return `<div class="metaLine">${escapeHtml(label)}：${escapeHtml(v)}</div>`;
};

const wireSectionLabeledLine = (label, raw) => {
	const v = String(raw ?? '').trim();
	if (!v) return '';
	return `<div>${escapeHtml(label)}：${escapeHtml(v)}</div>`;
};

const wireSectionPlainLine = (raw) => {
	const v = String(raw ?? '').trim();
	if (!v) return '';
	return `<div>${escapeHtml(v)}</div>`;
};

const companyInfoPlainLine = (raw) => {
	const v = String(raw ?? '').trim();
	if (!v) return '';
	return `<div>${escapeHtml(v)}</div>`;
};

export const buildStatementPdfHtml = ({
	customerName,
	asOfDate,
	customerPhone,
	customerEmail,
	customerAddress,
	customerOrganizationLine,
	salesRepEntries,
	lines,
	totalAR,
	tenantLogoUrl,
	tenantDisplayName,
	templateOverrides,
	fontFaceCss = STATEMENT_PDF_FONT_FACE_CSS,
}) => {
	const tpl = mergeStatementPdfTemplate(templateOverrides);
	const rows = lines
		.map((row) => {
			const contractNumber = numberCellHtml(row.contractNumber);
			const invoiceNumber = numberCellHtml(row.invoiceNumber);
			const startDate = escapeHtml(row.startDate || '-');
			const orderId = escapeHtml(row.orderId || '-');
			const paymentStage = paymentStageHtmlWithTermDays(row.paymentStage, row.paymentTermDays);
			const dueDate = escapeHtml(row.dueDate || '-');
			const overdueDays = escapeHtml(row.overdueDays || '0');
			const currencyCode = String(row.currency ?? tpl.currencyCode ?? '')
				.trim()
				|| 'CNY';
			const currencyCell = escapeHtml(currencyCode);
			const balance = formatStatementMoney(row.accountsReceivable);

			return `
				<tr>
					<td style="text-align:center;">${contractNumber}</td>
					<td style="text-align:center;">${invoiceNumber}</td>
					<td style="text-align:center;">${orderId}<div style="font-size:${tpl.tableCellSubFontSize};color:${tpl.tableCellSubColor};">${paymentStage}</div></td>
					<td style="text-align:center;">
						<div>${startDate}</div>
						<div>${dueDate}</div>
					</td>
					<td style="text-align:center;">${overdueDays}</td>
					<td style="text-align:center;">${currencyCell}</td>
					<td style="text-align:right;">${balance}</td>
				</tr>
			`;
		})
		.join('');

	const theadCells = tpl.text.tableHeaders
		.slice(0, STATEMENT_PDF_TABLE_COLUMN_COUNT)
		.map((head) => `<th>${escapeHtml(String(head ?? ''))}</th>`)
		.join('');

	const safeCustomerName = escapeHtml(customerName || '—');
	const safeCustomerSectionHeading = escapeHtml(String(tpl.text.customerTitle || tpl.text.billTo || '').trim());
	const safeAsOfDate = escapeHtml(asOfDate || '—');
	const entries = Array.isArray(salesRepEntries) ? salesRepEntries : [];
	const salesRepHtml =
		entries.length === 0
			? `<div class="metaLine">—</div>`
			: entries
					.map(({ name, phone, email }) => {
						const nm = escapeHtml(String(name ?? '').trim() || '—');
						return `<div style="margin-bottom:8px;"><div class="metaLine"><b>${nm}</b></div>${metaLabelValueDiv(tpl.text.customerPhoneLabel, phone)}${metaLabelValueDiv(tpl.text.customerEmailLabel, email)}</div>`;
					})
					.join('');
	const custPhone = optionalMetaLine(tpl.text.customerPhoneLabel, customerPhone);
	const custEmail = optionalMetaLine(tpl.text.customerEmailLabel, customerEmail);
	const custAddr = optionalMetaLine(tpl.text.customerAddressLabel, customerAddress);
	const custOrg = optionalMetaLine(tpl.text.customerOrganizationLabel, customerOrganizationLine);
	const safeTenantDisplayName = escapeHtml(tenantDisplayName || 'Genechamp');
	const safeTenantLogoUrl = tenantLogoUrl ? escapeHtml(tenantLogoUrl) : '';
	const logoContent = safeTenantLogoUrl
		? `<img src="${safeTenantLogoUrl}" alt="tenant-logo" style="max-width: ${tpl.logoMaxWidth}; max-height: ${tpl.logoMaxHeight}; object-fit: contain;" />`
		: `<div style="font-size:42px;font-weight:700;color:${tpl.logoFallbackColor};">${safeTenantDisplayName}</div>`;

	return `
		<!doctype html>
		<html lang="zh-CN">
		<head>
			<meta charset="UTF-8" />
			<style>
				${fontFaceCss}
				${STATEMENT_PDF_CSS}
			</style>
		</head>
		<body>
			<div class="page">
				<div class="row">
					<div class="box" style="flex:${tpl.headerLeftFlex};">
						<div style="font-weight:700;font-size:${tpl.companyNameFontSize};">${escapeHtml(String(tpl.companyInfo.name ?? '').trim())}</div>
						${companyInfoPlainLine(tpl.companyInfo.line1)}
						${companyInfoPlainLine(tpl.companyInfo.line2)}
						${companyInfoPlainLine(tpl.companyInfo.line3)}
						${companyInfoPlainLine(tpl.companyInfo.contactLine1)}
						${companyInfoPlainLine(tpl.companyInfo.contactLine2)}
					</div>
					<div style="flex:${tpl.headerRightFlex};display:flex;justify-content:flex-end;align-items:center;">
						${logoContent}
					</div>
				</div>

				<div class="h1">${tpl.text.documentTitle}</div>
				<div style="margin:-2px 0 10px;color:#333;font-size:14px;font-weight:700;">
					${escapeHtml(tpl.text.asOfDate)}：${safeAsOfDate}
				</div>

				<div class="box" style="padding:0;">
					<div class="row" style="gap:0;">
						<div style="flex:7;padding:8px;border-right:1px solid #111;min-width:0;white-space:normal;">
							<div style="font-weight:700;margin:0 0 ${STATEMENT_PDF_TEMPLATE.customerSectionHeadingMarginBottom};">${safeCustomerSectionHeading}</div>
							<div style="margin:0 0 ${STATEMENT_PDF_TEMPLATE.customerLegalNameMarginBottom};">${safeCustomerName}</div>
							${custPhone}${custEmail}${custAddr}${custOrg}
						</div>
						<div style="flex:5;padding:8px;min-width:0;white-space:normal;">
							<div style="font-weight:700;margin:0 0 ${STATEMENT_PDF_TEMPLATE.customerSectionHeadingMarginBottom};">${tpl.text.salesRepLabel}</div>
							${salesRepHtml}
						</div>
					</div>
				</div>

				<div class="tableWrap">
					<table>
						<thead>
							<tr>
								${theadCells}
							</tr>
						</thead>
						<tbody>
							${rows || `<tr><td colspan="${STATEMENT_PDF_TABLE_COLUMN_COUNT}" style="text-align:center;color:${tpl.emptyCellColor};padding:${tpl.emptyCellPadding};">${tpl.text.emptyLine}</td></tr>`}
						</tbody>
					</table>
				</div>

				<div class="totalRow">
					<div class="label">${tpl.text.totalLabel}</div>
					<div class="currency">${tpl.currencyCode}</div>
					<div class="amount">${formatStatementMoney(totalAR)}</div>
				</div>

				<div class="box small" style="margin-top:20px;">
					<b>${tpl.text.remarkLabel}</b>${tpl.text.remarkText}
				</div>

				<div class="box" style="padding:0;margin-top:20px;">
					<div class="sectionTitle">${tpl.text.paymentTitle}</div>
					<div class="footerGrid">
						<div class="footerCol small">
							<div><b>${tpl.text.wire}</b></div>
							${wireSectionLabeledLine('企业名称', tpl.text.wireEnterpriseName)}
							${wireSectionLabeledLine('税号', tpl.text.wireTaxId)}
							${wireSectionLabeledLine('地址', tpl.text.wireAddress)}
							${wireSectionLabeledLine('电话', tpl.text.wirePhone)}
							${wireSectionPlainLine(tpl.text.wireBank)}
							${wireSectionPlainLine(tpl.text.wireAccount)}
							${String(tpl.text.wireEnterpriseName ?? '').trim() ? '' : `<div>户名：${escapeHtml(tpl.companyInfo.name)}</div>`}
						</div>
						<div class="footerCol small">
							<div><b>${tpl.text.check}</b></div>
							${wireSectionPlainLine(tpl.text.checkMail)}
							${wireSectionPlainLine(tpl.text.checkExpress)}
							${wireSectionPlainLine(tpl.text.checkLine3)}
							${wireSectionPlainLine(tpl.text.checkLine4)}
						</div>
					</div>
				</div>
			</div>
		</body>
		</html>
	`;
};
